import { type TransientWalletStatus } from '@cpg/common';

import { authRequest } from './client';
import {
  type CreateMerchantInput,
  type ListMerchantTransientWalletsResponse,
  type ListWebhookDeliveriesResponse,
  type MerchantAccountItem,
  type MerchantActivityResponse,
  type MerchantApiKeyItem,
  type MerchantAssetItem,
  type MerchantLedgerItem,
  type MerchantPolicyItem,
  type MerchantResponse,
  type MerchantStatsResponse,
  type MerchantTariffOptionItem,
  type MerchantWebhookResponse,
  type TariffDetailResponse,
  type UpdateMerchantInput,
} from './types';

// ─── backoffice-api merchants client ───────────────────────────────────────
//
// Thin fetch wrappers around backoffice-api's `/v1/merchants` endpoints. No
// pagination/filters on the list endpoint (backend doesn't support any).
// `listMerchants`/`getMerchant` require MERCHANTS_VIEWER (or SUPERADMIN);
// `createMerchant`/`updateMerchant` require MERCHANTS_MANAGER (or
// SUPERADMIN) — enforced by the backend, mirrored client-side via
// `hasRole`-gated controls in the route component. The Task 25 sub-resource
// reads below are all MERCHANTS_VIEWER (or SUPERADMIN), except
// `getMerchantLedgers` (FINANCE_VIEWER) and `setMerchantAssetDisabled`
// (MERCHANTS_MANAGER) — same non-hierarchical role split the backend
// enforces.

export function listMerchants(): Promise<MerchantResponse[]> {
  return authRequest<MerchantResponse[]>('/merchants');
}

export function getMerchant(id: string): Promise<MerchantResponse> {
  return authRequest<MerchantResponse>(`/merchants/${id}`);
}

export function createMerchant(input: CreateMerchantInput): Promise<MerchantResponse> {
  return authRequest<MerchantResponse>('/merchants', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateMerchant(id: string, input: UpdateMerchantInput): Promise<MerchantResponse> {
  return authRequest<MerchantResponse>(`/merchants/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function getMerchantStats(id: string): Promise<MerchantStatsResponse> {
  return authRequest<MerchantStatsResponse>(`/merchants/${id}/stats`);
}

export function getMerchantAccounts(id: string): Promise<MerchantAccountItem[]> {
  return authRequest<MerchantAccountItem[]>(`/merchants/${id}/accounts`);
}

export function getMerchantTransientWallets(
  id: string,
  params: { page?: number; pageSize?: number; status?: TransientWalletStatus } = {},
): Promise<ListMerchantTransientWalletsResponse> {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.status !== undefined) query.set('status', params.status);
  const qs = query.toString();
  return authRequest<ListMerchantTransientWalletsResponse>(
    `/merchants/${id}/transient-wallets${qs ? `?${qs}` : ''}`,
  );
}

export function getMerchantLedgers(id: string): Promise<MerchantLedgerItem[]> {
  return authRequest<MerchantLedgerItem[]>(`/merchants/${id}/ledgers`);
}

export function getMerchantAssets(id: string): Promise<MerchantAssetItem[]> {
  return authRequest<MerchantAssetItem[]>(`/merchants/${id}/assets`);
}

export function setMerchantAssetDisabled(
  id: string,
  assetId: string,
  disabled: boolean,
): Promise<MerchantAssetItem> {
  return authRequest<MerchantAssetItem>(`/merchants/${id}/assets/${assetId}/disabled`, {
    method: 'PUT',
    body: JSON.stringify({ disabled }),
  });
}

export function getMerchantPolicies(id: string): Promise<MerchantPolicyItem[]> {
  return authRequest<MerchantPolicyItem[]>(`/merchants/${id}/policies`);
}

export function getMerchantWebhook(id: string): Promise<MerchantWebhookResponse | null> {
  return authRequest<MerchantWebhookResponse | null>(`/merchants/${id}/webhook`);
}

export function getMerchantWebhookDeliveries(
  id: string,
  params: { page?: number; pageSize?: number } = {},
): Promise<ListWebhookDeliveriesResponse> {
  const query = new URLSearchParams();
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  const qs = query.toString();
  return authRequest(`/merchants/${id}/webhook-deliveries${qs ? `?${qs}` : ''}`);
}

export function getMerchantApiKeys(id: string): Promise<MerchantApiKeyItem[]> {
  return authRequest<MerchantApiKeyItem[]>(`/merchants/${id}/api-keys`);
}

export function getMerchantActivity(id: string): Promise<MerchantActivityResponse> {
  return authRequest<MerchantActivityResponse>(`/merchants/${id}/activity`);
}

export function getMerchantTariffOptions(): Promise<MerchantTariffOptionItem[]> {
  return authRequest<MerchantTariffOptionItem[]>('/merchants/tariff-options');
}

export function getMerchantTariff(id: string): Promise<TariffDetailResponse | null> {
  return authRequest<TariffDetailResponse | null>(`/merchants/${id}/tariff`);
}
